<script setup lang="ts">
import FooterGrandNom from "~/components/layout/FooterGrandNom.vue";
/**
 * FooterLite — footer allégé des pages publiques d'ENTITÉS (fiches asso, à
 * terme cagnottes/événements/adhésions).
 *
 * Pourquoi (Karim 2026-08-05, inspiré du pattern HelloAsso) :
 *  - CONVERSION : sur une page où le visiteur peut donner/adhérer, chaque lien
 *    produit du footer complet est une porte de sortie. Ici : liens légaux +
 *    UN seul lien produit (« Inscrire mon association » = acquisition).
 *  - SEO : moins de boilerplate répété → meilleur ratio contenu unique, et le
 *    maillage se concentre sur les liens contextuels du corps de page
 *    (cagnottes/événements de l'asso, sections « à découvrir »).
 *
 * Le footer complet (AppFooter) reste servi partout ailleurs (home, /logiciel,
 * blog, listings /recherche…).
 */
const registerLink = useRegisterLink();
const { trackSignupClick } = useAnalytics();
const { t } = useI18n();
const localePath = useLocalePath();

const links = computed(() => [
  { label: t("navigation.footerLite.cgu"), to: localePath({ path: "/terms" }) },
  { label: t("navigation.footerLite.confidentialite"), to: localePath({ path: "/privacy" }) },
  { label: t("navigation.footerLite.mentionsLegales"), to: localePath({ path: "/mentions-legales" }) },
  { label: t("navigation.footerLite.charteEthique"), to: localePath({ path: "/charte-ethique" }) },
  { label: t("navigation.footerLite.contact"), to: localePath({ path: "/contact" }) },
  { label: t("navigation.footerLite.guideDesAssociations"), to: "/blog" },
]);

// Tout en bas, Safari (iPhone) montre le fond de la page sous sa barre :
// noir comme ce pied de page, et non une bande blanche (Karim 2026-10-13).
const pied = useTemplateRef<HTMLElement>("pied");
useFondSousLePied(pied);
</script>

<template>
  <!-- Aucune marge haute ici : l'espacement est décidé par la page qui monte
       ce footer (Karim 2026-08-08). -->
  <!-- `relative z-10` : passe au-dessus des bannières fixes (`fixed z-0`) des
       pages /a, /b et fiches assos — sinon la bannière recouvrait le haut du
       footer en bas de page (Karim 2026-10-07). Détail dans AppFooter. -->
  <footer ref="pied" class="relative z-10 bg-black text-white">
    <!-- CTA (même accroche que le footer complet, version COMPACTE et discrète) -->
    <div class="border-b border-slate-800">
      <UContainer>
        <div
          class="py-6 flex flex-col lg:flex-row items-center justify-between gap-5"
        >
          <div class="text-center lg:text-left">
            <h3 class="text-xl lg:text-2xl font-bold mb-1">
              {{ t("navigation.footerLite.pretAModerniserTitre") }}
            </h3>
            <p class="text-slate-400 text-sm lg:text-base">
              {{ t("navigation.footerLite.pretAModerniserTexte") }}
            </p>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-2.5">
            <NuxtLink
              v-register-cta="'pied-de-page-allege'"
              :to="registerLink"
              class="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 hover:bg-slate-100 active:bg-gray-200 font-semibold text-sm px-5 py-2.5 transition-colors"
              @click="trackSignupClick('footer')"
            >
              {{ t("navigation.footerLite.inscrireAssociationGratuitement") }}
              <UIcon name="i-lucide-arrow-right" class="w-4 h-4" />
            </NuxtLink>
            <CalendlyButton
              :label="t('navigation.footerLite.demoGratuite')"
              class="px-5 py-2.5 text-sm font-semibold"
              icon-wrapper-class="w-6 h-6"
            />
          </div>
        </div>
      </UContainer>
    </div>

    <UContainer>
      <div class="py-8 flex flex-col items-center gap-5">
        <!-- Rangée de liens légaux + acquisition -->
        <nav
          :aria-label="t('navigation.footerLite.liensDeBasDePage')"
          class="flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
        >
          <NuxtLink
            v-for="link in links"
            :key="link.to"
            :to="link.to"
            class="text-sm font-medium text-slate-300 hover:text-white transition-colors"
          >
            {{ link.label }}
          </NuxtLink>
        </nav>

        <div class="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
          <p class="text-sm text-slate-400">
            © {{ new Date().getFullYear() }} <span class="font-semibold text-slate-300">Donoor</span> · {{ t("navigation.footerLite.tousDroitsReserves") }}
          </p>
          <SelecteurLangue />
        </div>

        <div class="max-w-2xl text-center space-y-1.5">
          <p class="text-xs text-slate-500 leading-relaxed">
            {{ t("navigation.footerLite.pitchLigne1") }}
          </p>
          <p class="text-xs text-slate-500 leading-relaxed">
            {{ t("navigation.footerLite.pitchLigne2") }}
          </p>
        </div>
      </div>
    </UContainer>
    <!-- « Donoor » en très grand, gravé, le bas coupé par le bord (2026-10-13) -->
    <FooterGrandNom class="mt-2 overflow-hidden" />
  </footer>
</template>
